import React, { useState } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { ChevronLeft, ChevronRight } from 'lucide-react'
import { useAuthStore } from '../stores/authStore'

interface OnboardingData {
  name: string
  usage: string
  projectTypes: string[]
  goals: string[]
  currentApps: string[]
}

const onboardingSteps = [
  {
    id: 'welcome',
    component: 'WelcomeStep'
  },
  {
    id: 'name',
    component: 'NameStep'
  },
  {
    id: 'usage',
    component: 'UsageStep'
  },
  {
    id: 'projects',
    component: 'ProjectsStep'
  },
  {
    id: 'goals',
    component: 'GoalsStep'
  },
  {
    id: 'apps',
    component: 'AppsStep'
  },
  {
    id: 'value',
    component: 'ValueStep'
  }
]

export default function OnboardingPage() {
  const { user, setUser } = useAuthStore()
  const [currentStep, setCurrentStep] = useState(0)
  const [onboardingData, setOnboardingData] = useState<OnboardingData>({
    name: '',
    usage: '',
    projectTypes: [],
    goals: [],
    currentApps: []
  })

  const nextStep = () => {
    if (currentStep < onboardingSteps.length - 1) {
      setCurrentStep(currentStep + 1)
    }
  }

  const handleOnboardingComplete = async () => {
    try {
      // Save the name to user profile if provided
      if (onboardingData.name && user) {
        setUser({
          ...user,
          name: onboardingData.name
        })
      }

      // Mark onboarding as complete in backend
      const token = localStorage.getItem('kuraAuthToken')
      if (token) {
        await fetch('/api/auth/complete-onboarding', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          }
        })

        // Update user in store
        if (user) {
          setUser({
            ...user,
            onboardingCompleted: true
          })
        }
      }

      window.location.href = '/library'
    } catch (error) {
      console.error('Failed to complete onboarding:', error)
      // Still redirect even if API call fails
      window.location.href = '/library'
    }
  }

  const prevStep = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1)
    }
  }

  const updateData = (field: keyof OnboardingData, value: any) => {
    setOnboardingData(prev => ({
      ...prev,
      [field]: value
    }))
  }

  const renderStep = () => {
    const step = onboardingSteps[currentStep]
    
    switch (step.component) {
      case 'WelcomeStep':
        return <WelcomeStep onNext={nextStep} />
      case 'NameStep':
        return <NameStep data={onboardingData.name} onUpdate={(value) => updateData('name', value)} onNext={nextStep} onComplete={handleOnboardingComplete} />
      case 'UsageStep':
        return <UsageStep data={onboardingData.usage} onUpdate={(value) => updateData('usage', value)} onNext={nextStep} onPrev={prevStep} onComplete={handleOnboardingComplete} />
      case 'ProjectsStep':
        return <ProjectsStep data={onboardingData.projectTypes} onUpdate={(value) => updateData('projectTypes', value)} onNext={nextStep} onPrev={prevStep} onComplete={handleOnboardingComplete} />
      case 'GoalsStep':
        return <GoalsStep data={onboardingData.goals} onUpdate={(value) => updateData('goals', value)} onNext={nextStep} onPrev={prevStep} onComplete={handleOnboardingComplete} />
      case 'AppsStep':
        return <AppsStep data={onboardingData.currentApps} onUpdate={(value) => updateData('currentApps', value)} onNext={nextStep} onPrev={prevStep} onComplete={handleOnboardingComplete} />
      case 'ValueStep':
        return <ValueStep onComplete={handleOnboardingComplete} onPrev={prevStep} />
      default:
        return null
    }
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center p-4">
      <div className="w-full max-w-2xl relative">
        {/* Step Content */}
        <AnimatePresence mode="wait">
          <motion.div
            key={currentStep}
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -20 }}
            transition={{ duration: 0.3 }}
          >
            {renderStep()}
          </motion.div>
        </AnimatePresence>
      </div>
    </div>
  )
}

// Welcome Step Component
const WelcomeStep = ({ onNext }: { onNext: () => void }) => (
  <div className="text-center">
    <motion.div
      initial={{ scale: 0.8, opacity: 0 }}
      animate={{ scale: 1, opacity: 1 }}
      transition={{ duration: 0.5 }}
      className="mb-8"
    >
      <div className="w-16 h-16 rounded-lg bg-gray-100 dark:bg-gray-800 flex items-center justify-center mx-auto mb-6">
        <img src="/iconextn.png" alt="Memtrail" className="w-12 h-12 object-contain" />
      </div>
      <h1 className="text-4xl font-bold text-gray-900 dark:text-white mb-4">
        Welcome to your digital mind.
      </h1>
      <p className="text-lg text-gray-600 dark:text-gray-400 mb-8">
        Let's get you started with Memtrail
      </p>
    </motion.div>
    
    <motion.button
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: 0.3, duration: 0.3 }}
      onClick={onNext}
      className="bg-black dark:bg-white text-white dark:text-black px-8 py-3 rounded-lg font-medium hover:bg-gray-800 dark:hover:bg-gray-200 transition-colors"
    >
      Let's begin
    </motion.button>
  </div>
)

// Name Step Component
const NameStep = ({ data, onUpdate, onNext, onComplete }: { data: string, onUpdate: (value: string) => void, onNext: () => void, onComplete: () => void }) => (
  <div className="text-center">
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
    >
      <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-2">
        Welcome!
      </h1>
      <h2 className="text-2xl font-semibold text-gray-900 dark:text-white mb-8">
        What's your name?
      </h2>
      
      <div className="max-w-md mx-auto">
        <input
          type="text"
          value={data}
          onChange={(e) => onUpdate(e.target.value)}
          placeholder="Your name"
          className="w-full px-4 py-3 text-lg border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100"
          autoFocus
        />
        
        <div className="flex gap-3 mt-6">
          <motion.button
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2, duration: 0.3 }}
            onClick={onNext}
            disabled={!data.trim()}
            className="flex-1 bg-black dark:bg-white text-white dark:text-black px-6 py-3 rounded-lg font-medium hover:bg-gray-800 dark:hover:bg-gray-200 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Continue
          </motion.button>
          <motion.button
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2, duration: 0.3 }}
            onClick={onComplete}
            className="px-6 py-3 text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200 transition-colors"
          >
            Skip
          </motion.button>
        </div>
      </div>
    </motion.div>
  </div>
)

// Usage Step Component
const UsageStep = ({ data, onUpdate, onNext, onPrev, onComplete }: { data: string, onUpdate: (value: string) => void, onNext: () => void, onPrev: () => void, onComplete: () => void }) => {
  const options = [
    { id: 'personal', label: 'For personal projects', icon: '📐' },
    { id: 'work', label: 'For work', icon: '💼' },
    { id: 'study', label: 'For study', icon: '🎓' }
  ]

  return (
    <div className="text-center">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
      >
        <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-2">
          Hey {data || 'there'}!
        </h1>
        <h2 className="text-2xl font-semibold text-gray-900 dark:text-white mb-4">
          How do you plan to use Memtrail?
        </h2>
        <p className="text-gray-600 dark:text-gray-400 mb-8">
          This will help Memtrail's AI customize your experience.
        </p>
        
        <div className="max-w-md mx-auto space-y-3">
          {options.map((option) => (
            <motion.button
              key={option.id}
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.1 * options.indexOf(option), duration: 0.3 }}
              onClick={() => onUpdate(option.id)}
              className={`w-full p-4 rounded-lg border-2 transition-all ${
                data === option.id
                  ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/30'
                  : 'border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600 bg-white dark:bg-gray-800'
              }`}
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <span className="text-2xl">{option.icon}</span>
                  <span className="text-left font-medium text-gray-900 dark:text-gray-100">{option.label}</span>
                </div>
                <div className={`w-4 h-4 rounded-full border-2 ${
                  data === option.id ? 'border-blue-500 bg-blue-500' : 'border-gray-300'
                }`} />
              </div>
            </motion.button>
          ))}
        </div>
        
        <div className="flex justify-between items-center mt-8">
          <button
            onClick={onPrev}
            className="flex items-center gap-2 text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200 transition-colors"
          >
            <ChevronLeft className="w-4 h-4" />
            Back
          </button>
          <div className="flex gap-3">
            <button
              onClick={onNext}
              disabled={!data}
              className="bg-black dark:bg-white text-white dark:text-black px-6 py-3 rounded-lg font-medium hover:bg-gray-800 dark:hover:bg-gray-200 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Continue
            </button>
            <button
              onClick={onComplete}
              className="text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200 transition-colors px-4 py-3"
            >
              Skip
            </button>
          </div>
        </div>
      </motion.div>
    </div>
  )
}

// Projects Step Component
const ProjectsStep = ({ data, onUpdate, onNext, onPrev, onComplete }: { data: string[], onUpdate: (value: string[]) => void, onNext: () => void, onPrev: () => void, onComplete: () => void }) => {
  const projectTypes = [
    { id: 'creative', label: 'Creative', icon: '💡' },
    { id: 'research', label: 'Research', icon: '🔍' },
    { id: 'shopping', label: 'Shopping', icon: '🏷️' },
    { id: 'travel', label: 'Planning a trip', icon: '🏛️' },
    { id: 'health', label: 'Health', icon: '🏃' },
    { id: 'learning', label: 'Learning', icon: '📚' }
  ]

  const toggleProject = (projectId: string) => {
    if (data.includes(projectId)) {
      onUpdate(data.filter(id => id !== projectId))
    } else {
      onUpdate([...data, projectId])
    }
  }

  return (
    <div className="text-center">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
      >
        <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-2">
          What kind of projects?
        </h1>
        <p className="text-gray-600 dark:text-gray-400 mb-8">
          This will help Memtrail's AI customize your experience.
        </p>
        
        <div className="max-w-md mx-auto space-y-3">
          {projectTypes.map((project) => (
            <motion.button
              key={project.id}
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.1 * projectTypes.indexOf(project), duration: 0.3 }}
              onClick={() => toggleProject(project.id)}
              className={`w-full p-4 rounded-lg border-2 transition-all ${
                data.includes(project.id)
                  ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/30'
                  : 'border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600 bg-white dark:bg-gray-800'
              }`}
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <span className="text-2xl">{project.icon}</span>
                  <span className="text-left font-medium text-gray-900 dark:text-gray-100">{project.label}</span>
                </div>
                <div className={`w-4 h-4 rounded border-2 ${
                  data.includes(project.id) ? 'border-blue-500 bg-blue-500' : 'border-gray-300'
                }`} />
              </div>
            </motion.button>
          ))}
        </div>
        
        <div className="flex justify-between items-center mt-8">
          <button
            onClick={onPrev}
            className="flex items-center gap-2 text-gray-600 hover:text-gray-800 transition-colors"
          >
            <ChevronLeft className="w-4 h-4" />
            Back
          </button>
          <div className="flex gap-3">
            <button
              onClick={onNext}
              className="bg-black dark:bg-white text-white dark:text-black px-6 py-3 rounded-lg font-medium hover:bg-gray-800 dark:hover:bg-gray-200 transition-colors"
            >
              Continue
            </button>
            <button
              onClick={onComplete}
              className="text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200 transition-colors px-4 py-3"
            >
              Skip
            </button>
          </div>
        </div>
      </motion.div>
    </div>
  )
}

// Goals Step Component
const GoalsStep = ({ data, onUpdate, onNext, onPrev, onComplete }: { data: string[], onUpdate: (value: string[]) => void, onNext: () => void, onPrev: () => void, onComplete: () => void }) => {
  const goals = [
    'Find what I need when I need it',
    'Stop losing important information',
    'Keep everything in one place',
    'Capture ideas before I forget them',
    'Gather my ideas and inspiration',
    'Spend less time organizing'
  ]

  const toggleGoal = (goal: string) => {
    if (data.includes(goal)) {
      onUpdate(data.filter(g => g !== goal))
    } else {
      onUpdate([...data, goal])
    }
  }

  return (
    <div className="text-center">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
      >
        <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-2">
          What is your goal for using Memtrail?
        </h1>
        <p className="text-gray-600 dark:text-gray-400 mb-8">
          This will help Memtrail's AI customize your experience.
        </p>
        
        <div className="max-w-md mx-auto space-y-3">
          {goals.map((goal, index) => (
            <motion.button
              key={goal}
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.1 * index, duration: 0.3 }}
              onClick={() => toggleGoal(goal)}
              className={`w-full p-4 rounded-lg border-2 transition-all ${
                data.includes(goal)
                  ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/30'
                  : 'border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600 bg-white dark:bg-gray-800'
              }`}
            >
              <div className="flex items-center justify-between">
                <span className="text-left font-medium text-gray-900 dark:text-gray-100">{goal}</span>
                <div className={`w-4 h-4 rounded border-2 ${
                  data.includes(goal) ? 'border-blue-500 bg-blue-500' : 'border-gray-300'
                }`} />
              </div>
            </motion.button>
          ))}
        </div>
        
        <div className="flex justify-between items-center mt-8">
          <button
            onClick={onPrev}
            className="flex items-center gap-2 text-gray-600 hover:text-gray-800 transition-colors"
          >
            <ChevronLeft className="w-4 h-4" />
            Back
          </button>
          <div className="flex gap-3">
            <button
              onClick={onNext}
              className="bg-black dark:bg-white text-white dark:text-black px-6 py-3 rounded-lg font-medium hover:bg-gray-800 dark:hover:bg-gray-200 transition-colors"
            >
              Continue
            </button>
            <button
              onClick={onComplete}
              className="text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200 transition-colors px-4 py-3"
            >
              Skip
            </button>
          </div>
        </div>
      </motion.div>
    </div>
  )
}

// Apps Step Component
const AppsStep = ({ data, onUpdate, onNext, onPrev, onComplete }: { data: string[], onUpdate: (value: string[]) => void, onNext: () => void, onPrev: () => void, onComplete: () => void }) => {
  const apps = [
    { id: 'dropbox', label: 'Dropbox', icon: '📦' },
    { id: 'google-drive', label: 'Google Drive', icon: '🔺' },
    { id: 'evernote', label: 'Evernote', icon: '🐘' },
    { id: 'notion', label: 'Notion', icon: 'N' },
    { id: 'raindrop', label: 'Raindrop', icon: '☁️' },
    { id: 'mymind', label: 'MyMind', icon: '👁️' }
  ]

  const toggleApp = (appId: string) => {
    if (data.includes(appId)) {
      onUpdate(data.filter(id => id !== appId))
    } else {
      onUpdate([...data, appId])
    }
  }

  return (
    <div className="text-center">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
      >
        <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-2">
          What apps do you currently pay for?
        </h1>
        <p className="text-gray-600 dark:text-gray-400 mb-8">
          This will help Memtrail's AI customize your experience.
        </p>
        
        <div className="max-w-md mx-auto space-y-3">
          {apps.map((app, index) => (
            <motion.button
              key={app.id}
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.1 * index, duration: 0.3 }}
              onClick={() => toggleApp(app.id)}
              className={`w-full p-4 rounded-lg border-2 transition-all ${
                data.includes(app.id)
                  ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/30'
                  : 'border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600 bg-white dark:bg-gray-800'
              }`}
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <span className="text-2xl">{app.icon}</span>
                  <span className="text-left font-medium text-gray-900 dark:text-gray-100">{app.label}</span>
                </div>
                <div className={`w-4 h-4 rounded border-2 ${
                  data.includes(app.id) ? 'border-blue-500 bg-blue-500' : 'border-gray-300'
                }`} />
              </div>
            </motion.button>
          ))}
        </div>
        
        <div className="flex justify-between items-center mt-8">
          <button
            onClick={onPrev}
            className="flex items-center gap-2 text-gray-600 hover:text-gray-800 transition-colors"
          >
            <ChevronLeft className="w-4 h-4" />
            Back
          </button>
          <div className="flex gap-3">
            <button
              onClick={onNext}
              className="bg-black dark:bg-white text-white dark:text-black px-6 py-3 rounded-lg font-medium hover:bg-gray-800 dark:hover:bg-gray-200 transition-colors"
            >
              Continue
            </button>
            <button
              onClick={onComplete}
              className="text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200 transition-colors px-4 py-3"
            >
              Skip
            </button>
          </div>
        </div>
      </motion.div>
    </div>
  )
}

// Value Step Component
const ValueStep = ({ onComplete, onPrev }: { onComplete: () => void, onPrev: () => void }) => (
  <div className="text-center">
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
    >
      <div className="mb-8">
        <div className="text-6xl mb-6">💰</div>
        <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-4">
          Memtrail could save you <span className="text-blue-600 dark:text-blue-400">$50-100</span> a year
        </h1>
        <p className="text-gray-600 dark:text-gray-400 mb-8">
          Based on a typical price of your current apps.
        </p>
      </div>
      
      <div className="max-w-md mx-auto space-y-4 mb-8">
        <div className="flex items-center gap-3 text-left">
          <div className="w-8 h-8 bg-gray-100 dark:bg-gray-800 rounded-lg flex items-center justify-center">
            <span className="text-lg">📁</span>
          </div>
          <span className="text-gray-700 dark:text-gray-300">Everything in one place</span>
        </div>
        <div className="flex items-center gap-3 text-left">
          <div className="w-8 h-8 bg-gray-100 dark:bg-gray-800 rounded-lg flex items-center justify-center">
            <span className="text-lg">⚡</span>
          </div>
          <span className="text-gray-700 dark:text-gray-300">Be more productive</span>
        </div>
        <div className="flex items-center gap-3 text-left">
          <div className="w-8 h-8 bg-gray-100 dark:bg-gray-800 rounded-lg flex items-center justify-center">
            <span className="text-lg">💰</span>
          </div>
          <span className="text-gray-700 dark:text-gray-300">Save money</span>
        </div>
      </div>
      
      <div className="flex justify-between items-center">
          <button
            onClick={onPrev}
            className="flex items-center gap-2 text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200 transition-colors"
          >
            <ChevronLeft className="w-4 h-4" />
            Back
          </button>
          <div className="flex gap-3">
            <button
              onClick={onComplete}
              className="bg-black dark:bg-white text-white dark:text-black px-8 py-3 rounded-lg font-medium hover:bg-gray-800 dark:hover:bg-gray-200 transition-colors"
            >
              Get Started
            </button>
            <button
              onClick={onComplete}
              className="text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200 transition-colors px-4 py-3"
            >
              Skip
            </button>
          </div>
        </div>
    </motion.div>
  </div>
)
